// ══════════════════════════════════════════════════════════════════════
// GQ Backtest Engine — institutional-grade single-asset & multi-asset
// Deterministic given (strategy, ohlcv, config, seed). Emits trade log
// + equity curve consumable by stats.js fullKPIs.
//
// Strategy DSL (JSON-serialisable, language-agnostic):
//   {
//     indicators: [ {id, type:'ema'|'sma'|'rsi'|'atr'|'bbands'|'macd',
//                    source:'close', params:{period:14, ...}} ],
//     entry: { long:[expr], short:[expr] },      // expressions in DSL below
//     exit:  { long:[expr], short:[expr] },
//     sizing: { mode:'fixedPct'|'fixedDollar'|'kelly'|'halfKelly'|'atrTarget'|'riskParity',
//               value: 0.10, riskPct: 0.01, atrK: 2, kellyClamp: 0.5 },
//     execution:{ orderType:'market'|'limit'|'stop'|'stopLimit'|'trailingStop',
//                 fillTiming:'next_open'|'this_close'|'intra_bar_limit',
//                 latency: 0 },
//     costs: { commissionBps: 5, slippageBps: 2, slippageModel:'constant'|'volImpact',
//              borrowBps: 50, makerBps: 1, takerBps: 5 },
//     constraints:{ maxLeverage:1, maxPositionPct:1, allowShort:false,
//                   maxOpenPositions:1, betaNeutral:false },
//     rebalance: { schedule:'daily'|'weekly'|'monthly'|'signal', dayOfWeek:1 }
//   }
//
// Expressions DSL (string):
//   "ema9 > ema21"
//   "rsi14 < 30 and price > ema50"
//   "macd_signal > 0 and atr14 > 0.5"
//   Operators: > < >= <= == != and or not + - * /
//   Reserved: price (close), open, high, low, volume, position, bar
// ══════════════════════════════════════════════════════════════════════

(function (root) {
  const stats = (root && root.GQ && root.GQ.stats)
    || (typeof require !== 'undefined' ? require('./stats.js') : null);

  // ────────────────────────────────────────────────────────────────────
  // Deterministic RNG (xorshift32) — seedable for reproducibility
  // ────────────────────────────────────────────────────────────────────
  function rng(seed = 1) {
    let s = seed >>> 0;
    return () => {
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      return ((s >>> 0) % 1_000_000) / 1_000_000;
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Indicators — vectorised over the input series
  // ────────────────────────────────────────────────────────────────────
  function sma(arr, p) {
    const out = new Array(arr.length).fill(null);
    let sum = 0;
    for (let i = 0; i < arr.length; i++) {
      sum += arr[i];
      if (i >= p) sum -= arr[i - p];
      if (i >= p - 1) out[i] = sum / p;
    }
    return out;
  }
  function ema(arr, p) {
    const out = new Array(arr.length).fill(null);
    const k = 2 / (p + 1);
    let prev = null;
    for (let i = 0; i < arr.length; i++) {
      if (i < p - 1) continue;
      if (prev === null) {
        let s = 0;
        for (let j = 0; j <= i; j++) s += arr[j];
        prev = s / (i + 1);
      } else {
        prev = arr[i] * k + prev * (1 - k);
      }
      out[i] = prev;
    }
    return out;
  }
  function rsi(arr, p = 14) {
    const out = new Array(arr.length).fill(null);
    let gain = 0, loss = 0;
    for (let i = 1; i < arr.length; i++) {
      const d = arr[i] - arr[i - 1];
      const g = d > 0 ? d : 0, l = d < 0 ? -d : 0;
      if (i <= p) {
        gain += g; loss += l;
        if (i === p) {
          gain /= p; loss /= p;
          out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
        }
      } else {
        gain = (gain * (p - 1) + g) / p;
        loss = (loss * (p - 1) + l) / p;
        out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
      }
    }
    return out;
  }
  function atr(high, low, close, p = 14) {
    const tr = new Array(close.length).fill(null);
    for (let i = 0; i < close.length; i++) {
      if (i === 0) tr[i] = high[i] - low[i];
      else tr[i] = Math.max(
        high[i] - low[i],
        Math.abs(high[i] - close[i - 1]),
        Math.abs(low[i] - close[i - 1])
      );
    }
    return ema(tr, p);
  }
  function bbands(arr, p = 20, k = 2) {
    const mid = sma(arr, p);
    const upper = new Array(arr.length).fill(null);
    const lower = new Array(arr.length).fill(null);
    for (let i = p - 1; i < arr.length; i++) {
      let s = 0;
      for (let j = i - p + 1; j <= i; j++) s += (arr[j] - mid[i]) ** 2;
      const sd = Math.sqrt(s / p);
      upper[i] = mid[i] + k * sd;
      lower[i] = mid[i] - k * sd;
    }
    return { mid, upper, lower };
  }
  function macd(arr, fast = 12, slow = 26, signal = 9) {
    const ef = ema(arr, fast), es = ema(arr, slow);
    const line = arr.map((_, i) =>
      ef[i] != null && es[i] != null ? ef[i] - es[i] : null);
    const lineFilled = line.map(x => x ?? 0);
    const sig = ema(lineFilled, signal).map((v, i) => line[i] != null ? v : null);
    const hist = line.map((v, i) => v != null && sig[i] != null ? v - sig[i] : null);
    return { line, signal: sig, hist };
  }

  function buildIndicators(specs, ohlcv) {
    const close = ohlcv.map(b => b.close);
    const high  = ohlcv.map(b => b.high);
    const low   = ohlcv.map(b => b.low);
    const open  = ohlcv.map(b => b.open);
    const vol   = ohlcv.map(b => b.volume);
    const series = { close, high, low, open, volume: vol, price: close };
    const out = {};
    for (const spec of specs) {
      const src = series[spec.source || 'close'] || close;
      switch (spec.type) {
        case 'sma':    out[spec.id] = sma(src, spec.params.period); break;
        case 'ema':    out[spec.id] = ema(src, spec.params.period); break;
        case 'rsi':    out[spec.id] = rsi(src, spec.params.period); break;
        case 'atr':    out[spec.id] = atr(high, low, close, spec.params.period); break;
        case 'bbands': {
          const b = bbands(src, spec.params.period, spec.params.k || 2);
          out[spec.id + '_mid'] = b.mid;
          out[spec.id + '_up']  = b.upper;
          out[spec.id + '_dn']  = b.lower;
          out[spec.id] = b.mid;
          break;
        }
        case 'macd': {
          const m = macd(src, spec.params.fast || 12, spec.params.slow || 26, spec.params.signal || 9);
          out[spec.id]         = m.line;
          out[spec.id + '_sig'] = m.signal;
          out[spec.id + '_hist'] = m.hist;
          break;
        }
        default: out[spec.id] = src;
      }
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Expression evaluator — tokenised → RPN → evaluated per bar
  // ────────────────────────────────────────────────────────────────────
  const PREC = { 'or': 1, 'and': 2, 'not': 3,
                 '==': 4, '!=': 4, '<': 4, '<=': 4, '>': 4, '>=': 4,
                 '+': 5, '-': 5, '*': 6, '/': 6 };

  function tokenize(expr) {
    const tokens = [];
    let i = 0;
    while (i < expr.length) {
      const c = expr[i];
      if (/\s/.test(c)) { i++; continue; }
      if (/[a-zA-Z_]/.test(c)) {
        let j = i;
        while (j < expr.length && /[a-zA-Z0-9_]/.test(expr[j])) j++;
        const t = expr.slice(i, j);
        tokens.push({ type: ['and','or','not'].includes(t) ? 'op' : 'id', value: t });
        i = j;
      } else if (/[0-9.]/.test(c)) {
        let j = i;
        while (j < expr.length && /[0-9.]/.test(expr[j])) j++;
        tokens.push({ type: 'num', value: parseFloat(expr.slice(i, j)) });
        i = j;
      } else if ('()'.includes(c)) {
        tokens.push({ type: 'paren', value: c });
        i++;
      } else {
        // multi-char operators
        const two = expr.slice(i, i + 2);
        if (['==','!=','<=','>='].includes(two)) {
          tokens.push({ type: 'op', value: two }); i += 2;
        } else if ('<>+-*/'.includes(c)) {
          tokens.push({ type: 'op', value: c }); i++;
        } else { i++; }
      }
    }
    return tokens;
  }
  function toRPN(tokens) {
    const out = [], ops = [];
    for (const t of tokens) {
      if (t.type === 'num' || t.type === 'id') out.push(t);
      else if (t.type === 'op') {
        while (ops.length && ops[ops.length - 1].type === 'op' &&
               PREC[ops[ops.length - 1].value] >= PREC[t.value]) out.push(ops.pop());
        ops.push(t);
      } else if (t.value === '(') ops.push(t);
      else if (t.value === ')') {
        while (ops.length && ops[ops.length - 1].value !== '(') out.push(ops.pop());
        ops.pop();
      }
    }
    while (ops.length) out.push(ops.pop());
    return out;
  }
  function evalRPN(rpn, ctx) {
    const stack = [];
    for (const t of rpn) {
      if (t.type === 'num') stack.push(t.value);
      else if (t.type === 'id') stack.push(ctx[t.value]);
      else {
        if (t.value === 'not') { stack.push(!stack.pop()); continue; }
        const b = stack.pop(), a = stack.pop();
        switch (t.value) {
          case 'and': stack.push(a && b); break;
          case 'or':  stack.push(a || b); break;
          case '==':  stack.push(a == b); break;
          case '!=':  stack.push(a != b); break;
          case '<':   stack.push(a <  b); break;
          case '<=':  stack.push(a <= b); break;
          case '>':   stack.push(a >  b); break;
          case '>=':  stack.push(a >= b); break;
          case '+':   stack.push(a +  b); break;
          case '-':   stack.push(a -  b); break;
          case '*':   stack.push(a *  b); break;
          case '/':   stack.push(b !== 0 ? a / b : 0); break;
        }
      }
    }
    return stack[0];
  }
  function compileExpr(expr) {
    if (!expr || expr === 'false') return () => false;
    if (expr === 'true') return () => true;
    const rpn = toRPN(tokenize(expr));
    return (ctx) => Boolean(evalRPN(rpn, ctx));
  }

  // ────────────────────────────────────────────────────────────────────
  // Position sizing
  // ────────────────────────────────────────────────────────────────────
  function calcSize(sizing, equity, price, atrVal, recentTrades) {
    const mode = sizing?.mode || 'fixedPct';
    const value = sizing?.value ?? 0.10;
    switch (mode) {
      case 'fixedDollar':
        return Math.floor(value / price);
      case 'fixedPct':
        return Math.floor((equity * value) / price);
      case 'atrTarget': {
        const k = sizing.atrK || 2;
        if (!atrVal) return Math.floor((equity * 0.05) / price);
        const riskPerShare = atrVal * k;
        const riskBudget = equity * (sizing.riskPct || 0.01);
        return Math.floor(riskBudget / Math.max(riskPerShare, price * 0.001));
      }
      case 'kelly':
      case 'halfKelly': {
        if (!recentTrades || recentTrades.length < 10) return Math.floor((equity * 0.05) / price);
        const w = stats.winRate(recentTrades);
        const pr = stats.payoffRatio(recentTrades);
        if (pr === 0) return 0;
        let f = Math.max(0, w - (1 - w) / pr);
        if (mode === 'halfKelly') f *= 0.5;
        f = Math.min(f, sizing.kellyClamp || 0.5);
        return Math.floor((equity * f) / price);
      }
      case 'riskParity':
        // Placeholder for multi-asset; single-asset just falls to fixedPct
        return Math.floor((equity * value) / price);
      default:
        return Math.floor((equity * 0.10) / price);
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Fill / slippage model
  // ────────────────────────────────────────────────────────────────────
  function applySlippage(price, side, costs, bar) {
    const bpsBase = (costs?.slippageBps ?? 2) / 10000;
    let slip = bpsBase;
    if (costs?.slippageModel === 'volImpact' && bar?.volume) {
      // Almgren-Chriss-lite: linear in size/volume (we approximate using bar range)
      const range = (bar.high - bar.low) / bar.close;
      slip = bpsBase + range * 0.05;
    }
    // Buyer pays up, seller takes down
    return side === 'long' ? price * (1 + slip) : price * (1 - slip);
  }
  function commission(notional, costs, isTaker = true) {
    const bps = isTaker ? (costs?.takerBps ?? costs?.commissionBps ?? 5)
                        : (costs?.makerBps ?? 1);
    return notional * bps / 10000;
  }

  // ────────────────────────────────────────────────────────────────────
  // Order types — work intra-bar against next bar
  // ────────────────────────────────────────────────────────────────────
  function fillOrder(order, nextBar, costs) {
    // returns { filled:bool, price:number, partial?:number }
    const { type, side, limit, stop, qty } = order;
    if (!nextBar) return { filled: false };
    const o = nextBar.open, h = nextBar.high, l = nextBar.low, c = nextBar.close;
    switch (type) {
      case 'market':
        return { filled: true, price: applySlippage(o, side, costs, nextBar) };
      case 'limit': {
        // Long limit: triggers if low <= limit. Fill at min(limit, open).
        if (side === 'long' && l <= limit)  return { filled: true, price: applySlippage(Math.min(limit, o), side, costs, nextBar) };
        if (side === 'short' && h >= limit) return { filled: true, price: applySlippage(Math.max(limit, o), side, costs, nextBar) };
        return { filled: false };
      }
      case 'stop': {
        if (side === 'long' && h >= stop)  return { filled: true, price: applySlippage(Math.max(stop, o), side, costs, nextBar) };
        if (side === 'short' && l <= stop) return { filled: true, price: applySlippage(Math.min(stop, o), side, costs, nextBar) };
        return { filled: false };
      }
      case 'stopLimit': {
        const stopHit = side === 'long' ? h >= stop : l <= stop;
        if (!stopHit) return { filled: false };
        // Then act like limit
        if (side === 'long' && l <= limit)  return { filled: true, price: applySlippage(Math.min(limit, o), side, costs, nextBar) };
        if (side === 'short' && h >= limit) return { filled: true, price: applySlippage(Math.max(limit, o), side, costs, nextBar) };
        return { filled: false };
      }
      case 'trailingStop':
        // Caller updates `stop` each bar; here we just check
        if (side === 'long' && l <= stop)  return { filled: true, price: applySlippage(Math.min(stop, o), side, costs, nextBar) };
        if (side === 'short' && h >= stop) return { filled: true, price: applySlippage(Math.max(stop, o), side, costs, nextBar) };
        return { filled: false };
      default:
        return { filled: true, price: applySlippage(o, side, costs, nextBar) };
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Single-asset backtest — primary entry point
  // ────────────────────────────────────────────────────────────────────
  function run(strategy, ohlcv, opts = {}) {
    if (!ohlcv || ohlcv.length < 30) throw new Error('Need ≥30 bars');
    const config = Object.assign({
      capital: 10000,
      seed: 42,
      benchmark: null,    // array of equity matching ohlcv length
      slice: null,        // [startIdx, endIdx] for walk-forward
    }, opts);
    const _ = rng(config.seed);

    const ind = buildIndicators(strategy.indicators || [], ohlcv);
    const entryLong  = compileExpr((strategy.entry?.long || []).join(' and ') || 'false');
    const exitLong   = compileExpr((strategy.exit?.long  || []).join(' and ') || 'false');
    const entryShort = strategy.constraints?.allowShort
      ? compileExpr((strategy.entry?.short || []).join(' and ') || 'false') : () => false;
    const exitShort  = strategy.constraints?.allowShort
      ? compileExpr((strategy.exit?.short  || []).join(' and ') || 'false') : () => false;

    const sizing = strategy.sizing || { mode: 'fixedPct', value: 0.10 };
    const exec   = strategy.execution || { orderType: 'market', fillTiming: 'next_open' };
    const costs  = strategy.costs || { commissionBps: 5, slippageBps: 2 };
    const cons   = strategy.constraints || { maxLeverage: 1, allowShort: false };

    const i0 = (config.slice && config.slice[0]) || 0;
    const i1 = (config.slice && config.slice[1]) || ohlcv.length - 1;

    let equity = config.capital;
    let cash = equity;
    let position = 0;          // signed shares
    let entryPx = 0;
    let entryBar = 0;
    let highWatermark = entryPx;
    let lowWatermark = entryPx;
    const trades = [];
    const equityCurve = [];
    const exposure = [];

    for (let i = i0; i <= i1; i++) {
      const bar = ohlcv[i];
      const nextBar = ohlcv[i + 1];
      const ctx = {
        price: bar.close, open: bar.open, high: bar.high, low: bar.low, volume: bar.volume,
        position, bar: i,
      };
      for (const k in ind) ctx[k] = ind[k][i] != null ? ind[k][i] : 0;

      // Track MAE/MFE while in trade
      if (position !== 0) {
        if (position > 0) {
          highWatermark = Math.max(highWatermark, bar.high);
          lowWatermark  = Math.min(lowWatermark,  bar.low);
        } else {
          highWatermark = Math.max(highWatermark, bar.high);
          lowWatermark  = Math.min(lowWatermark,  bar.low);
        }
      }

      // ── Exit logic
      if (position > 0 && exitLong(ctx) && nextBar) {
        const fill = fillOrder({ type: exec.orderType, side: 'short', qty: position }, nextBar, costs);
        if (fill.filled) {
          const notional = Math.abs(position) * fill.price;
          const fee = commission(notional, costs, exec.orderType !== 'limit');
          cash += position * fill.price - fee;
          const pnl = position * (fill.price - entryPx) - fee;
          trades.push({
            side: 'long',
            entryBar, exitBar: i + 1,
            entryTime: ohlcv[entryBar].time, exitTime: nextBar.time,
            entryPx, exitPx: fill.price, qty: position, pnl,
            pnlPct: pnl / (entryPx * Math.abs(position)),
            mae: (lowWatermark - entryPx) / entryPx,    // worst dip during trade (signed)
            mfe: (highWatermark - entryPx) / entryPx,
            holdBars: (i + 1) - entryBar,
            risk: Math.abs(entryPx * position) * (sizing.riskPct || 0.01),
          });
          position = 0;
        }
      } else if (position < 0 && exitShort(ctx) && nextBar) {
        const fill = fillOrder({ type: exec.orderType, side: 'long', qty: -position }, nextBar, costs);
        if (fill.filled) {
          const notional = Math.abs(position) * fill.price;
          const fee = commission(notional, costs, exec.orderType !== 'limit');
          cash += position * fill.price - fee;
          const pnl = -position * (entryPx - fill.price) - fee;
          trades.push({
            side: 'short',
            entryBar, exitBar: i + 1,
            entryTime: ohlcv[entryBar].time, exitTime: nextBar.time,
            entryPx, exitPx: fill.price, qty: position, pnl,
            pnlPct: pnl / (entryPx * Math.abs(position)),
            mae: -(highWatermark - entryPx) / entryPx,
            mfe: -(lowWatermark - entryPx) / entryPx,
            holdBars: (i + 1) - entryBar,
            risk: Math.abs(entryPx * position) * (sizing.riskPct || 0.01),
          });
          position = 0;
        }
      }

      // ── Entry logic
      if (position === 0 && nextBar) {
        const atrSpec = (strategy.indicators || []).find(s => s.type === 'atr');
        const atrVal = atrSpec ? ind[atrSpec.id][i] : null;
        if (entryLong(ctx)) {
          const qty = calcSize(sizing, equity, bar.close, atrVal, trades.slice(-30));
          if (qty > 0) {
            const fill = fillOrder({ type: exec.orderType, side: 'long', qty }, nextBar, costs);
            if (fill.filled) {
              const notional = qty * fill.price;
              const fee = commission(notional, costs, exec.orderType !== 'limit');
              cash -= notional + fee;
              position = qty;
              entryPx = fill.price; entryBar = i + 1;
              highWatermark = fill.price; lowWatermark = fill.price;
            }
          }
        } else if (entryShort(ctx)) {
          const qty = calcSize(sizing, equity, bar.close, atrVal, trades.slice(-30));
          if (qty > 0) {
            const fill = fillOrder({ type: exec.orderType, side: 'short', qty }, nextBar, costs);
            if (fill.filled) {
              const notional = qty * fill.price;
              const fee = commission(notional, costs, exec.orderType !== 'limit');
              cash += notional - fee;
              position = -qty;
              entryPx = fill.price; entryBar = i + 1;
              highWatermark = fill.price; lowWatermark = fill.price;
            }
          }
        }
      }

      equity = cash + position * bar.close;
      equityCurve.push({ t: bar.time, v: equity });
      exposure.push({ t: bar.time, e: position === 0 ? 0 : (position * bar.close) / equity });
    }

    // Close any open position at end
    if (position !== 0) {
      const last = ohlcv[i1];
      const fee = commission(Math.abs(position) * last.close, costs);
      cash += position * last.close - fee;
      trades.push({
        side: position > 0 ? 'long' : 'short',
        entryBar, exitBar: i1,
        entryTime: ohlcv[entryBar].time, exitTime: last.time,
        entryPx, exitPx: last.close, qty: position,
        pnl: position * (last.close - entryPx) - fee,
        pnlPct: (last.close - entryPx) / entryPx * Math.sign(position),
        mae: (lowWatermark - entryPx) / entryPx,
        mfe: (highWatermark - entryPx) / entryPx,
        holdBars: i1 - entryBar,
        forced_close: true,
      });
      equity = cash;
      position = 0;
    }

    // KPIs
    const equityValues = equityCurve.map(p => p.v);
    const benchEquity = config.benchmark ? config.benchmark.slice(i0, i1 + 1) : null;
    const kpis = stats.fullKPIs({ equity: equityValues, trades, benchEquity });

    return {
      strategy_snapshot: strategy,
      config,
      equity_curve: equityCurve,
      exposure,
      trades,
      metrics: kpis,
      final_equity: equity,
      bars_evaluated: i1 - i0 + 1,
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Walk-forward analysis
  // ────────────────────────────────────────────────────────────────────
  function walkForward(strategy, ohlcv, opts = {}) {
    const { folds = 5, trainPct = 0.7 } = opts;
    const n = ohlcv.length;
    const foldSize = Math.floor(n / folds);
    const results = [];
    for (let f = 0; f < folds; f++) {
      const s = f * foldSize;
      const e = Math.min(n - 1, s + foldSize);
      const split = Math.floor(s + (e - s) * trainPct);
      const inSample  = run(strategy, ohlcv, { ...opts, slice: [s, split] });
      const outSample = run(strategy, ohlcv, { ...opts, slice: [split + 1, e] });
      results.push({
        fold: f,
        train: { range: [s, split], metrics: inSample.metrics },
        test:  { range: [split + 1, e], metrics: outSample.metrics },
        deltaSharpe: (inSample.metrics.sharpe || 0) - (outSample.metrics.sharpe || 0),
      });
    }
    const overfittingFlag = stats.mean(results.map(r => r.deltaSharpe)) > 0.5;
    return { folds: results, overfittingFlag };
  }

  // ────────────────────────────────────────────────────────────────────
  // Multi-asset portfolio backtest — simple basket rebalanced periodically
  // ────────────────────────────────────────────────────────────────────
  function runPortfolio(strategy, ohlcvBySymbol, opts = {}) {
    // ohlcvBySymbol: { SPY:[bars...], QQQ:[bars...] } aligned by index
    const symbols = Object.keys(ohlcvBySymbol);
    const n = ohlcvBySymbol[symbols[0]].length;
    const weights = (strategy.portfolio?.weights) || symbols.map(_ => 1 / symbols.length);
    const capital = opts.capital || 10000;
    const equityCurve = [];
    let v = capital;
    for (let i = 0; i < n; i++) {
      let dayReturn = 0;
      for (let s = 0; s < symbols.length; s++) {
        if (i === 0) continue;
        const bars = ohlcvBySymbol[symbols[s]];
        const r = (bars[i].close - bars[i - 1].close) / bars[i - 1].close;
        dayReturn += weights[s] * r;
      }
      v *= (1 + dayReturn);
      equityCurve.push({ t: ohlcvBySymbol[symbols[0]][i].time, v });
    }
    const kpis = stats.fullKPIs({ equity: equityCurve.map(p => p.v), trades: [] });
    return { equity_curve: equityCurve, metrics: kpis, symbols, weights };
  }

  // ────────────────────────────────────────────────────────────────────
  // Public surface
  // ────────────────────────────────────────────────────────────────────
  const api = {
    run, runPortfolio, walkForward,
    buildIndicators, compileExpr,
    sma, ema, rsi, atr, bbands, macd,
    calcSize, fillOrder, applySlippage, commission,
    rng,
  };
  if (root) { root.GQ = root.GQ || {}; root.GQ.engine = api; }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
